import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Quiz, QuizState } from '../types/quiz.types';
import { fetchQuiz, fetchRandomQuizId } from '../services/quizService';

interface QuizContextType {
  quiz: Quiz | null;
  quizState: QuizState;
  loading: boolean;
  error: string | null;
  loadQuiz: (id: string) => Promise<void>;
  selectOption: (optionId: string) => void;
  submitAnswer: () => void;
  resetQuiz: () => void;
  toggleMarkForLater: () => void;
  goToNextQuestion: () => Promise<void>;
  goToPreviousQuestion: () => Promise<void>;
}

const initialQuizState: QuizState = {
  currentQuizId: null,
  selectedOption: null,
  isAnswered: false,
  isMarkedForLater: false,
  timeStarted: Date.now(),
  timeSpent: 0,
};

const QuizContext = createContext<QuizContextType | undefined>(undefined);

interface QuizProviderProps {
  children: ReactNode;
}

export const QuizProvider: React.FC<QuizProviderProps> = ({ children }) => {
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [quizState, setQuizState] = useState<QuizState>(initialQuizState);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Load a quiz by ID
  const loadQuiz = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await fetchQuiz(id);
      
      setQuiz(data.quiz);
      setQuizState({
        ...initialQuizState,
        currentQuizId: id,
        timeStarted: Date.now(),
      });
    } catch (error) {
      setError('Failed to load quiz. Please try again.');
      console.error('Error loading quiz:', error);
    } finally {
      setLoading(false);
    }
  };

  // Select an option
  const selectOption = (optionId: string) => {
    if (!quizState.isAnswered) {
      setQuizState({
        ...quizState,
        selectedOption: optionId,
      });
    }
  };

  // Submit the answer
  const submitAnswer = () => {
    if (quizState.selectedOption) {
      const timeSpent = Math.floor((Date.now() - quizState.timeStarted) / 1000);
      
      setQuizState({
        ...quizState,
        isAnswered: true,
        timeSpent,
      });
    }
  };

  // Reset the current quiz
  const resetQuiz = () => {
    setQuizState({
      ...quizState,
      selectedOption: null,
      isAnswered: false,
      timeStarted: Date.now(),
      timeSpent: 0,
    });
  };

  // Toggle mark for later
  const toggleMarkForLater = () => {
    setQuizState({
      ...quizState,
      isMarkedForLater: !quizState.isMarkedForLater,
    });
  };

  // Navigate to the next question
  const goToNextQuestion = async () => {
    // In a real app, this would navigate to the next question in a sequence
    // For this demo, we'll just load a random new quiz
    try {
      const newId = await fetchRandomQuizId();
      await loadQuiz(newId);
    } catch (error) {
      setError('Failed to load next question. Please try again.');
    }
  };

  // Navigate to the previous question
  const goToPreviousQuestion = async () => {
    // In a real app, this would navigate to the previous question in a sequence
    // For this demo, we'll just load a random new quiz
    try {
      const newId = await fetchRandomQuizId();
      await loadQuiz(newId);
    } catch (error) {
      setError('Failed to load previous question. Please try again.');
    }
  };

  // Load a random quiz when the component mounts
  useEffect(() => {
    const loadRandomQuiz = async () => {
      try {
        const randomId = await fetchRandomQuizId();
        await loadQuiz(randomId);
      } catch (error) {
        setError('Failed to load a quiz. Please refresh the page.');
      }
    };

    loadRandomQuiz();
  }, []);

  return (
    <QuizContext.Provider
      value={{
        quiz,
        quizState,
        loading,
        error,
        loadQuiz,
        selectOption,
        submitAnswer,
        resetQuiz,
        toggleMarkForLater,
        goToNextQuestion,
        goToPreviousQuestion,
      }}
    >
      {children}
    </QuizContext.Provider>
  );
};

// Custom hook to use the quiz context
export const useQuiz = (): QuizContextType => {
  const context = useContext(QuizContext);
  
  if (context === undefined) {
    throw new Error('useQuiz must be used within a QuizProvider');
  }
  
  return context;
}; 